import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { getCourses } from '../lib/api';
import { 
  Play, ArrowRight, Sparkles, Users, BookOpen, Award,
  Palette, Laptop, Trophy, Film, Box, Wand2, Scissors, 
  GraduationCap, Cpu
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.15 } }
};

// Icon mapping for courses
const courseIcons = {
  'Design Foundation': Palette,
  '3D Modeling': Box,
  'Rigging': Wand2,
  'Animation': Film,
  'Compositing': Scissors,
  'Bachelor': GraduationCap,
  'AI': Cpu,
  'default': BookOpen
};

const getCourseIcon = (title) => {
  for (const [key, icon] of Object.entries(courseIcons)) {
    if (title?.toLowerCase().includes(key.toLowerCase())) return icon;
  }
  return courseIcons.default;
};

const courseColors = [
  'from-emerald-500 to-teal-500',
  'from-blue-500 to-cyan-500',
  'from-purple-500 to-pink-500',
  'from-orange-500 to-amber-500',
  'from-pink-500 to-rose-500',
  'from-cyan-500 to-blue-500',
];

// Realistic stats for a startup with industry context
const stats = [
  { value: '₹1.2T', label: 'Indian Animation Market by 2030' },
  { value: '8+', label: 'Industry-Ready Programs' },
  { value: '35%', label: 'Annual Industry Growth' },
  { value: '1:5', label: 'Student-Mentor Ratio' },
];

export default function Home() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const coursesRes = await getCourses({ published_only: true });
      setCourses(coursesRes.data || []);
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToCourses = () => {
    document.getElementById('programs-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-cyan-50 to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900" />
        
        {/* Floating gradient orbs */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-r from-emerald-400/30 to-cyan-400/30 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-gradient-to-r from-purple-400/30 to-pink-400/30 rounded-full blur-3xl animate-float" style={{animationDelay: '-2s'}} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-400/20 to-cyan-400/20 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div 
              className="space-y-8"
              initial="initial"
              animate="animate"
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp}>
                <Badge className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-emerald-500 to-cyan-500 text-white border-0">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Now with AI-Powered Learning
                </Badge>
              </motion.div>

              <motion.h1 
                variants={fadeInUp}
                className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.1]"
              >
                <span className="text-gradient-rainbow">Unleash Your</span>
                <br />
                <span>Creative Vision</span>
              </motion.h1>

              <motion.p 
                variants={fadeInUp}
                className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl"
              >
                Join Visual Institute of Arts and transform your passion into a career. 
                From Design Foundation to AI Filmmaking - learn from industry experts.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="btn-gradient group h-14 px-8 text-lg"
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/apply')}
                  data-testid="hero-cta"
                >
                  {isAuthenticated ? 'Go to Dashboard' : 'Start Your Journey'}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="h-14 px-8 text-lg rounded-full border-2"
                  onClick={scrollToCourses}
                  data-testid="explore-courses"
                >
                  <Play className="mr-2 w-5 h-5" />
                  Explore Programs
                </Button>
              </motion.div>

              {/* Stats */}
              <motion.div 
                variants={fadeInUp}
                className="grid grid-cols-4 gap-6 pt-8 border-t border-border/50"
              >
                {stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-gradient-green">{stat.value}</div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right Content - Image Gallery */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                {/* Main Image - Abstract 3D Art */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1759267487608-23b5cffcb3a4?w=800"
                    alt="Abstract 3D Digital Art"
                    className="w-full aspect-[4/3] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  
                  {/* Floating thumbnails */}
                  <div className="absolute top-4 right-4 flex gap-2">
                    <img 
                      src="https://images.unsplash.com/photo-1764440093861-f047bbbf7d9a?w=100" 
                      alt="Motion Graphics" 
                      className="w-16 h-16 rounded-xl object-cover border-2 border-white/30 shadow-lg"
                    />
                    <img 
                      src="https://images.unsplash.com/photo-1766431187039-18a49b56fb2a?w=100" 
                      alt="3D Animation" 
                      className="w-16 h-16 rounded-xl object-cover border-2 border-white/30 shadow-lg"
                    />
                  </div>
                  
                  {/* Floating card */}
                  <div className="absolute bottom-6 left-6 right-6 p-5 rounded-2xl glass-card">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 flex items-center justify-center">
                        <Play className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-lg">{courses.length || 8} Industry Programs</p>
                        <p className="text-sm text-muted-foreground">3D • VFX • Animation • AI Filmmaking</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs-section" className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
              Our Programs
            </Badge>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
              <span className="text-gradient-purple">World-Class</span> Curriculum
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Industry-aligned programs designed to prepare you for a successful career in design and visual arts
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course, i) => {
              const IconComponent = getCourseIcon(course.title);
              const colorClass = courseColors[i % courseColors.length];
              
              // Get pricing from tiers
              const selfLearningPrice = course.pricing_tiers?.self_learning?.enabled !== false 
                ? course.pricing_tiers?.self_learning?.price 
                : null;
              const instructorLedPrice = course.pricing_tiers?.instructor_led?.enabled !== false 
                ? course.pricing_tiers?.instructor_led?.price 
                : null;
              const startingPrice = selfLearningPrice || instructorLedPrice || course.price || 0;
              const hasTwoPlans = selfLearningPrice && instructorLedPrice;
              
              return (
                <motion.div
                  key={course.course_id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card 
                    className="card-vibrant cursor-pointer bg-card overflow-hidden group"
                    onClick={() => navigate(`/courses/${course.course_id}`)}
                    data-testid={`course-card-${course.course_id}`}
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={course.thumbnail || 'https://images.unsplash.com/photo-1561070791-2526d30994b5'}
                        alt={course.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1561070791-2526d30994b5'; }}
                      />
                      <div className={`absolute inset-0 bg-gradient-to-t ${colorClass} opacity-60`} />
                      <Badge className="absolute top-4 left-4 bg-black/50 text-white border-0">
                        {course.course_id}
                      </Badge>
                      {hasTwoPlans && (
                        <Badge className="absolute top-4 right-4 bg-amber-500 text-white border-0">
                          2 Plans
                        </Badge>
                      )}
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center gap-2 text-white">
                          <IconComponent className="w-6 h-6" />
                          <span className="font-semibold">{course.duration_weeks ? `${course.duration_weeks} Weeks` : course.duration_months ? `${course.duration_months} Months` : '12 Weeks'}</span>
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="font-bold text-xl mb-2 line-clamp-2">{course.title}</h3>
                      <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                        {course.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div>
                          {hasTwoPlans && (
                            <span className="text-xs text-muted-foreground block">Starting from</span>
                          )}
                          <span className="text-lg font-bold text-gradient-green">
                            ₹{startingPrice.toLocaleString('en-IN')}
                          </span>
                        </div>
                        <Button variant="ghost" size="sm" className="text-primary font-semibold">
                          Learn More <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Placement Partners Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
              <Trophy className="w-4 h-4 mr-2" />
              Our Placement Partners
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Where Our <span className="text-gradient-rainbow">Alumni</span> Work
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our graduates are working at top animation studios and VFX companies across the globe
            </p>
          </motion.div>

          {/* Beautiful Company Names Grid */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 md:gap-12"
          >
            {[
              { name: 'PIXAR', gradient: 'from-blue-500 to-cyan-500' },
              { name: 'NETFLIX', gradient: 'from-red-500 to-rose-500' },
              { name: 'DNEG', gradient: 'from-purple-500 to-pink-500' },
              { name: 'SONY', gradient: 'from-blue-600 to-indigo-600' },
              { name: 'BLUE SKY', gradient: 'from-sky-400 to-blue-500' },
              { name: 'VAWWORLD', gradient: 'from-emerald-500 to-teal-500' },
              { name: 'PRIME FOCUS', gradient: 'from-orange-500 to-amber-500' },
              { name: 'FRAMESTORE', gradient: 'from-violet-500 to-purple-500' },
              { name: 'MPC', gradient: 'from-cyan-500 to-blue-500' },
              { name: 'WETA DIGITAL', gradient: 'from-green-500 to-emerald-500' },
              { name: 'ILM', gradient: 'from-yellow-500 to-orange-500' },
              { name: 'TATA ELXSI', gradient: 'from-indigo-500 to-blue-500' },
            ].map((company, i) => (
              <motion.div
                key={company.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="group text-center"
              >
                <h3 
                  className={`text-2xl md:text-3xl lg:text-4xl font-black tracking-wider bg-gradient-to-r ${company.gradient} bg-clip-text text-transparent 
                    hover:scale-110 transition-transform duration-300 cursor-default
                    drop-shadow-sm`}
                  style={{ fontFamily: "'Inter', 'SF Pro Display', -apple-system, sans-serif" }}
                >
                  {company.name}
                </h3>
              </motion.div>
            ))}
          </motion.div>
          
          {/* Stats below companies */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <div className="inline-flex items-center gap-8 px-8 py-4 rounded-2xl bg-muted/50 border border-border/50">
              <div>
                <p className="text-3xl font-bold text-gradient-green">500+</p>
                <p className="text-sm text-muted-foreground">Alumni Placed</p>
              </div>
              <div className="w-px h-12 bg-border" />
              <div>
                <p className="text-3xl font-bold text-gradient-purple">98%</p>
                <p className="text-sm text-muted-foreground">Placement Rate</p>
              </div>
              <div className="w-px h-12 bg-border" />
              <div>
                <p className="text-3xl font-bold text-gradient-rainbow">12+</p>
                <p className="text-sm text-muted-foreground">Countries</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why VIA Section */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white border-0">
                Why Choose VIA
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Where <span className="text-gradient-rainbow">Creativity</span>
                <br />Meets Technology
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                At Visual Institute of Arts, we blend traditional artistic principles with 
                cutting-edge technology. Our AI-powered learning platform, industry partnerships, 
                and expert faculty ensure you&apos;re ready for the future of design.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: Award, title: 'Industry Certified', desc: 'Recognized qualifications' },
                  { icon: Users, title: 'Expert Faculty', desc: '50+ industry professionals' },
                  { icon: Laptop, title: 'AI Integration', desc: 'Future-ready learning' },
                  { icon: Trophy, title: '98% Placement', desc: 'Career success rate' }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-r ${
                      ['from-emerald-500 to-teal-500', 'from-blue-500 to-cyan-500', 
                       'from-purple-500 to-pink-500', 'from-orange-500 to-amber-500'][i]
                    } flex items-center justify-center flex-shrink-0`}>
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Creative Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1633356122544-f134324a6cee"
                  alt="Creative workspace"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <p className="text-2xl font-bold mb-2">Learn from Industry Experts</p>
                  <p className="text-white/80">Get hands-on training with professionals from top studios</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-cyan-500 to-blue-500 opacity-90" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTJoMnYyem0wLTRoLTJ2LTJoMnYyem0tNC00aC0ydi0yaDJ2MnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Start Your Journey With Us
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
              Join thousands of creative professionals who started their journey with VIA. 
              Your dream career is just one click away.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg" 
                className="bg-white text-emerald-600 hover:bg-white/90 h-14 px-8 text-lg rounded-full font-semibold shadow-xl"
                onClick={scrollToCourses}
                data-testid="cta-enroll"
              >
                Enroll Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 h-14 px-8 text-lg rounded-full"
                onClick={() => navigate('/contact')}
              >
                Contact Admissions
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
